import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Button, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { MainContext } from "../../App";

import ClassDetails from "../../components/class/ClassDetails";
import ClassDays from "../../components/class/ClassDays";
import ClassStudents from "../../components/class/ClassStudents";
import TextLoading from "../../components/global/TextLoading";
import ModalConfirmDeleted from "../../components/class/modals/ModalConfirmDeleted";

import {
  UpdateClass,
  CreateClass,
  AddStudentsClass,
  DeleteStudentClass,
} from "../../api/class";
import { goToHref } from "../../components/global/GlobalTools";

const customStyles = {
  btns: {
    width: "fit-content",
  },
};

// dimensiones de la pantalla
const screenHeight = window.innerHeight;
const containerHeight = screenHeight - 170;

const urlParams = new URLSearchParams(window.location.search);
const teacherSource = urlParams.get("teacher") || false;

const baseUrl = window.location.href.split("?")[0];
window.history.replaceState({}, "", baseUrl);

const classTemplate = {
  // details
  optional: false,
  schoolCycle: "",
  schoolLevel: "",
  academicOffer: "",
  programLevel: "",
  gradeLevel: "",
  gradeGroup: "",
  subject: "",
  classTitle: "",
  classRoom: "",
  teacher: "",
  // schedules
  schedule: [],
  students: [],
  // TODO: actualmente en v1 esto se hace en un servicio aparte encadenado
};

const ClassProfile = () => {
  const [t, ready] = useTranslation(["class", "global"]);
  const history = useHistory();
  // Contexts
  const { permissions } = useContext(MainContext);

  const params = useParams();
  const classId = params.id;
  const isNewClass = classId === "new";

  const [validateSchema, setValidateSchema] = useState(null);

  const [schoolingFilters, setSchoolingFilters] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showModalConfirmDeleted, setShowModalConfirmDeleted] = useState(false);
  const [changeSchooling, setChangeSchooling] = useState(false);
  const [currentStudents, setCurrentStudents] = useState([]);
  const [classInformationChange, setClassInformationChange] = useState(false);

  useEffect(() => {
    const canCreateClass = permissions?.classes?.create;
    if (isNewClass && !canCreateClass) {
      history.push("/class");
    }
  }, []);

  /* Effect for loading initial translations */
  useEffect(() => {
    if (ready) {
      setTimeout(() => {
        // definir esquemas de validacion
        const schemaMain = Yup.object().shape({
          optional: Yup.boolean(),
          // schedule solo requerido si es clase nueva
          schedule: isNewClass
            ? Yup.array().min(
                1,
                t("class:classDetails.schedule") +
                  " " +
                  t("class:classDetails.fieldRequired"),
              )
            : Yup.array(),
          // schedule: Yup.array().required("Schedule required"),
          schoolCycle: Yup.object().required(
            t("class:classDetails.schoolCycle") +
              " " +
              t("class:classDetails.fieldRequired"),
          ),
          schoolLevel: Yup.object().required(
            t("class:classDetails.schoolLevel") +
              " " +
              t("class:classDetails.fieldRequired"),
          ),
          subject: Yup.object().required(
            t("class:classDetails.subject") +
              t("class:classDetails.fieldRequired"),
          ),
          classTitle: Yup.string(),
          classRoom: Yup.string(),
          teacher: Yup.object().required(
            t("class:classDetails.assignedTeacher") +
              t("class:classDetails.fieldRequired"),
          ),

          // gradeLevel y gradeGroup son condicionales
          academicOffer: Yup.object().when("schoolLevel", {
            is: (val) => val && val.value == "higher_education",
            then: Yup.object().required(
              t("class:classDetails.academicOffering") +
                " " +
                t("class:classDetails.fieldRequired"),
            ),
          }),
          programLevel: Yup.object().when("schoolLevel", {
            is: (val) => val && val.value == "higher_education",
            then: Yup.object().required(
              t("class:classDetails.programLevel") +
                " " +
                t("class:classDetails.fieldRequired"),
            ),
          }),
          gradeLevel: Yup.object().when("schoolLevel", {
            is: (val) => val && val.value != "higher_education",
            then: Yup.object().required(
              t("class:classDetails.grade") +
                " " +
                t("class:classDetails.fieldRequired"),
            ),
          }),
          gradeGroup: Yup.object().when("schoolLevel", {
            is: (val) => val && val.value != "higher_education",
            then: Yup.object().required(
              t("class:classDetails.group") +
                " " +
                t("class:classDetails.fieldRequired"),
            ),
          }),
        });
        setValidateSchema(schemaMain);
      }, 500);
    }
  }, [ready]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, 4000);
    }
  }, [error]);

  // Prevenir refresh/cierre y navegación interna si hay cambios no guardados
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (classInformationChange) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    let unblock;
    if (classInformationChange) {
      unblock = history.block((location) => {
        if (
          window.confirm(
            "Tienes cambios sin guardar. ¿Seguro que quieres salir?",
          )
        ) {
          if (unblock) unblock();
          history.push(location.pathname + (location.search || ""));
        }
        return false;
      });
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (unblock) unblock();
    };
  }, [classInformationChange, history]);

  const goBack = () => {
    // TODO: origenes de retorno varios
    if (teacherSource) {
      goToHref("/staff/" + teacherSource);
      return;
    }
    /* default */
    history.goBack();
  };

  const updateClass = (values) => {
    if (changeSchooling && currentStudents.length > 0) deteleCurrentStudents();
    let payload = {
      id: classId,
      optional_class: values.optional,
      class_title: values.classTitle,
      class_room: values.classRoom,
      teacher_id: values.teacher.id,
      subject_id: values.subject.id,
      schedule: values.schedule,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (changeSchooling) {
      if (values.schoolLevel.id == "higher_education") {
        // superior
        payload.program_id = values.academicOffer.id;
        payload.program_level_cycle_id = values.programLevel.id;
      } else {
        // basic
        payload.organization_school_level_cycle_id = values.schoolLevel.value;
        if (values.gradeGroup.id == 0) {
          // all groups
          payload.grade_level_id = values.gradeLevel.id;
        } else {
          // grade and group
          payload.grade_group_grade_level_id = values.gradeGroup.gggl;
        }
      }
    }
    setSaving(true);
    UpdateClass(payload).then(() => {
      if (changeSchooling && values.students?.length > 0) {
        addStudentsToClass(classId, values.students);
      }
      setSaving(false);
      setClassInformationChange(false);
      Cookies.set("refreshAttendance", "true");
    });
  };

  const deteleCurrentStudents = () => {
    const studentsIds = currentStudents.map((student) => student.id);
    const payload = {
      students_ids: studentsIds,
      class_id: classId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    DeleteStudentClass(payload).then(() => {});
  };

  const createClass = (values) => {
    let payload = {
      optional_class: values.optional,
      class_title: values.classTitle,
      class_room: values.classRoom,
      teacher_id: values.teacher.id,
      subject_id: values.subject.id,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      school_cycle_id: values.schoolCycle.id,

      schedule: values.schedule,
    };

    if (values.schoolLevel.id == "higher_education") {
      // superior
      payload.program_id = values.academicOffer.id;
      payload.program_level_cycle_id = values.programLevel.id;
    } else {
      // basic
      payload.organization_school_level_cycle_id = values.schoolLevel.value;
      if (values.gradeGroup.id == 0) {
        // all groups
        payload.grade_level_id = values.gradeLevel.id;
      } else {
        // grade and group
        payload.grade_group_grade_level_id = values.gradeGroup.gggl;
      }
    }

    setSaving(true);
    CreateClass(payload).then((response) => {
      if (response.status) {
        if (values.students.length > 0) {
          addStudentsToClass(response.data.id, values.students);
        } else {
          setSaving(false);
          history.push("/class");
        }
      } else {
        setSaving(false);
        setError(response.description || t("global:errors.general"));
      }
    });
  };

  const addStudentsToClass = (classId, studentList) => {
    const payload = {
      student_ids: studentList.map((student) => student.id),
      class_id: classId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    AddStudentsClass(payload).then(() => {
      setSaving(false);
      history.push("/class");
    });
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12} className="ps-0">
          <h6>
            <b role="button" onClick={() => goBack()}>
              <i className="bi bi-chevron-left"></i>
              {t("global:breadcrumds.goBack")}
            </b>
          </h6>
        </Col>
      </Row>
      <Formik
        initialValues={classTemplate}
        validationSchema={validateSchema}
        onSubmit={(values) => {
          if (isNewClass) {
            createClass(values);
          } else {
            updateClass(values);
          }
        }}
      >
        {(formikProps) => {
          const heightClassDetails =
            formikProps.values.schoolLevel?.value == "higher_education"
              ? 485
              : 405;
          const keypadCompensation = 50;
          const heightClassDays =
            containerHeight - heightClassDetails - keypadCompensation;
          const errorHeight = error ? 130 : 0;
          const mainContainerHeight = 750 + errorHeight;

          return (
            <form onSubmit={formikProps.handleSubmit}>
              <Row
                style={{
                  background: "#ffffff",
                  minHeight: mainContainerHeight + "px",
                }}
                className="py-3"
              >
                <Col
                  xs={12}
                  sm={5}
                  md={5}
                  lg={5}
                  xl={4}
                  xxl={4}
                  style={{
                    borderRight: "solid 1px gray",
                    paddingRight: "20px",
                    display: "flex",
                    flexDirection: "column",
                    height: `calc(100vh - 210px)`, // Usa el alto disponible menos el header
                    minHeight: 0, // Importante para que los hijos puedan hacer overflow
                  }}
                >
                  <Container fluid className="d-flex flex-column h-100 p-0">
                    {/* ClassDetails */}
                    <Row className="flex-grow-0">
                      <Col
                        md={12}
                        style={{
                          minHeights: heightClassDetails + "px",
                          flex: "0 1 auto",
                        }}
                      >
                        <ClassDetails
                          classId={classId}
                          formik={formikProps}
                          setValidateSchema={setValidateSchema}
                          setSchoolingFilters={setSchoolingFilters}
                          setChangeSchooling={setChangeSchooling}
                          changeSchooling={changeSchooling}
                          setClassInformationChange={setClassInformationChange}
                        />
                      </Col>
                    </Row>
                    {/* ClassDays */}
                    <Row className="flex-grow-1" style={{ minHeight: 205 }}>
                      <Col
                        md={12}
                        style={{
                          minHeights: heightClassDays + "px",
                          flex: "1 1 0",
                        }}
                      >
                        <ClassDays
                          classId={classId}
                          setFieldSchedule={(schedule) =>
                            formikProps.setFieldValue("schedule", schedule)
                          }
                          required={formikProps.errors?.schedule ? true : false}
                          setClassInformationChange={setClassInformationChange}
                          setParentError={setError}
                        />
                      </Col>
                    </Row>
                    {/** Show error message */}
                    {error && (
                      <Row>
                        <Col md={12} className="mt-3 mb-3">
                          <Alert variant="warning">{error}</Alert>
                        </Col>
                      </Row>
                    )}
                    {/* Buttons */}
                    <Row className="ps-2">
                      <Col xs="auto" className="p-0">
                        {!isNewClass && (
                          <Button
                            variant="outline-danger"
                            style={customStyles.btns}
                            disabled={!permissions.classes.create || saving}
                            onClick={() => setShowModalConfirmDeleted(true)}
                          >
                            {t("class:main.deleteClass")}
                          </Button>
                        )}
                      </Col>
                      <Col xs="auto" className="p-0 ms-auto">
                        <Button
                          id="cancelClassButton"
                          variant="outline-secondary"
                          style={customStyles.btns}
                          className="me-2"
                          onClick={() => goBack()}
                          disabled={!permissions.classes.create || saving}
                        >
                          {t("global:buttons.cancel")}
                        </Button>
                        <Button
                          id="saveClassButton"
                          type="submit"
                          variant="primary"
                          style={customStyles.btns}
                          disabled={
                            !permissions.classes.create ||
                            saving ||
                            (!isNewClass && !classInformationChange)
                          }
                        >
                          {saving ? (
                            <TextLoading
                              text={t("global:buttons.saving")}
                              variant="light"
                            />
                          ) : (
                            t("global:buttons.save")
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </Col>
                <Col xs={12} sm={7} md={7} lg={7} xl={8} xxl={8}>
                  <ClassStudents
                    classId={classId}
                    setFieldStudents={(studentList) =>
                      formikProps.setFieldValue("students", studentList)
                    }
                    studentsInClass={
                      isNewClass ? formikProps.values.students : currentStudents
                    }
                    schoolingFilters={schoolingFilters}
                    schoolCycleId={formikProps.values.schoolCycle.id}
                    changeSchooling={changeSchooling}
                    setCurrentStudents={setCurrentStudents}
                    classInfo={formikProps.values}
                  />
                </Col>
              </Row>
            </form>
          );
        }}
      </Formik>

      {showModalConfirmDeleted && (
        <ModalConfirmDeleted
          showModalConfirmDeleted={showModalConfirmDeleted}
          setShowModalConfirmDeleted={setShowModalConfirmDeleted}
          getClasses={() => history.push("/class")}
          items={[
            {
              original: {
                id: classId,
              },
            },
          ]}
        />
      )}
    </Container>
  );
};
export default ClassProfile;

ClassProfile.propTypes = {
  match: PropTypes.object,
};
